import {
  Box,
  BoxProps,
  CloseButton,
  HStack,
  Image,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { useLocalStorage } from '@grcs/hook/useLocalStorage';
import { UserRole } from '@grcs/types/UserRole';
import React from 'react';
import { FiBarChart, FiPlus, FiUserPlus, FiUsers } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import { NavItem } from './NavItem';
import { ILinkItemProps } from './SidebarItem';
import csrf_logo from '@grcs/images/csrf_logo.png';

interface ISidebarProps extends BoxProps {
  onClose: () => void;
  sidebarLinkItems: Array<ILinkItemProps>;
}

interface NavigationGroup {
  title: string;
  items: ILinkItemProps[];
}

export const SidebarContent: React.FC<ISidebarProps> = ({
  onClose,
  sidebarLinkItems,
  ...rest
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [{ role }] = useLocalStorage('user', {});

  const bg = useColorModeValue('white', 'gray.900');
  const border = useColorModeValue('gray.200', 'gray.700');
  const dividerColor = useColorModeValue('gray.200', 'gray.600');
  const groupTitleColor = useColorModeValue('gray.500', 'gray.400');
  const actionBg = useColorModeValue('gray.50', 'gray.800');
  const actionHover = useColorModeValue('gray.100', 'gray.700');

  // Extract parameters from pathname since useParams doesn't work at layout level
  const classMatch = pathname.match(/\/class\/([^/]+)/);
  const yearMatch = pathname.match(/\/(20\d{2})\//);
  const extractedClassId = classMatch ? classMatch[1] : null;
  const extractedYear = yearMatch ? yearMatch[1] : null;

  // Detect if we're in a class context
  const isInClassContext = pathname.includes('/class/') && extractedClassId;
  const isTeacher = role === UserRole.TEACHER;
  const isAdmin = role === UserRole.ADMIN;

  // Quick action handlers
  const handleCreateHomework = () => {
    navigate(`/teacher/${extractedYear}/class/${extractedClassId}/homeworks`);
  };

  const handleViewStudents = () => {
    navigate(`/teacher/${extractedYear}/class/${extractedClassId}/student`);
  };

  const handleViewAnalytics = () => {
    navigate(`/teacher/${extractedYear}/class/${extractedClassId}/analytics`);
  };

  const handleAddStudents = () => {
    navigate(`/teacher/${extractedYear}/class/${extractedClassId}/student/add`);
  };

  // Group navigation items logically
  const groupNavItems = (items: ILinkItemProps[]): NavigationGroup[] => {
    const groups: NavigationGroup[] = [];
    let currentGroup: NavigationGroup | null = null;

    items.forEach((item) => {
      if (item.name === 'Home') {
        groups.push({ title: '', items: [item] });
      } else if (
        ['Semester', 'Class', 'Teacher', 'Student'].includes(item.name)
      ) {
        if (!currentGroup || currentGroup.title !== 'Management') {
          currentGroup = { title: 'Management', items: [] };
          groups.push(currentGroup);
        }
        currentGroup.items.push(item);
      } else if (['Library', 'Leaderboard', 'Analytics'].includes(item.name)) {
        if (!currentGroup || currentGroup.title !== 'Content & Analytics') {
          currentGroup = { title: 'Content & Analytics', items: [] };
          groups.push(currentGroup);
        }
        currentGroup.items.push(item);
      } else {
        if (!currentGroup || currentGroup.title !== 'Content') {
          currentGroup = { title: 'Content', items: [] };
          groups.push(currentGroup);
        }
        currentGroup.items.push(item);
      }
    });

    return groups;
  };

  // Filter out Student Analytics when in class context to avoid confusion
  const filteredSidebarItems = isInClassContext
    ? sidebarLinkItems.filter((item) => item.name !== 'Student Analytics')
    : sidebarLinkItems;

  const navigationGroups = groupNavItems(filteredSidebarItems);

  return (
    <Box
      bg={bg}
      w={{ base: 'full', md: '260px' }}
      pos="fixed"
      h="full"
      borderRightWidth="1px"
      borderRightColor={border}
      boxShadow="lg"
      {...rest}
    >
      {/* Scrollable Content Area */}
      <Box
        pt="100px"
        pb="120px" // Space for footer
        px="6"
        h="full"
        overflowY="auto"
        css={{
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: useColorModeValue('#CBD5E0', '#4A5568'),
            borderRadius: '24px',
          },
        }}
      >
        {/* Mobile Close Button */}
        <Box
          mb="6"
          display={{ base: 'flex', md: 'none' }}
          justifyContent="flex-end"
        >
          <CloseButton onClick={onClose} />
        </Box>

        {/* Navigation Content */}
        <Stack spacing="6">
          {navigationGroups.map((group, groupIndex) => (
            <Box key={group.title || groupIndex}>
              {group.title && (
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  color={groupTitleColor}
                  textTransform="uppercase"
                  letterSpacing="wider"
                  mb="3"
                  px="3"
                >
                  {group.title}
                </Text>
              )}
              <Stack spacing="1">
                {group.items.map((link) => {
                  const isSelected = pathname === link.link;
                  return (
                    <NavItem
                      key={link.name}
                      icon={link.icon}
                      link={link.link}
                      isSelected={isSelected}
                    >
                      {link.name}
                    </NavItem>
                  );
                })}
              </Stack>
            </Box>
          ))}

          {/* Class Quick Actions */}
          {isInClassContext && (isTeacher || isAdmin) && (
            <Box>
              <Text
                fontSize="xs"
                fontWeight="semibold"
                color={groupTitleColor}
                textTransform="uppercase"
                letterSpacing="wider"
                mb="3"
                px="3"
              >
                Class Actions
              </Text>
              <Stack spacing="1">
                {isTeacher && (
                  <Box
                    px="3"
                    py="2"
                    borderRadius="md"
                    cursor="pointer"
                    transition="all 0.2s"
                    bg={actionBg}
                    _hover={{ bg: actionHover }}
                    onClick={handleCreateHomework}
                  >
                    <HStack spacing="3">
                      <FiPlus size={16} />
                      <Text fontSize="sm" fontWeight="medium">
                        Create Assignment
                      </Text>
                    </HStack>
                  </Box>
                )}

                <Box
                  px="3"
                  py="2"
                  borderRadius="md"
                  cursor="pointer"
                  transition="all 0.2s"
                  bg={actionBg}
                  _hover={{ bg: actionHover }}
                  onClick={handleViewStudents}
                >
                  <HStack spacing="3">
                    <FiUsers size={16} />
                    <Text fontSize="sm" fontWeight="medium">
                      {isAdmin ? 'Manage Students' : 'View Students'}
                    </Text>
                  </HStack>
                </Box>

                <Box
                  px="3"
                  py="2"
                  borderRadius="md"
                  cursor="pointer"
                  transition="all 0.2s"
                  bg={actionBg}
                  _hover={{ bg: actionHover }}
                  onClick={handleAddStudents}
                >
                  <HStack spacing="3">
                    <FiUserPlus size={16} />
                    <Text fontSize="sm" fontWeight="medium">
                      Add Students
                    </Text>
                  </HStack>
                </Box>

                <Box
                  px="3"
                  py="2"
                  borderRadius="md"
                  cursor="pointer"
                  transition="all 0.2s"
                  bg={actionBg}
                  _hover={{ bg: actionHover }}
                  onClick={handleViewAnalytics}
                >
                  <HStack spacing="3">
                    <FiBarChart size={16} />
                    <Text fontSize="sm" fontWeight="medium">
                      View Analytics
                    </Text>
                  </HStack>
                </Box>
              </Stack>
            </Box>
          )}
        </Stack>
      </Box>

      {/* Fixed Footer */}
      <Box
        pos="absolute"
        bottom="0"
        left="0"
        right="0"
        py="6"
        px="6"
        bg={bg}
        borderTop="1px solid"
        borderColor={dividerColor}
      >
        <Box textAlign="center">
          <Text
            fontSize="sm"
            fontWeight="medium"
            color={groupTitleColor}
            mb="1"
          >
            泓河开卷&trade;
          </Text>
          <Image src={csrf_logo} h="1.5em" w="auto" />
          <Text fontSize="xs" color="gray.400">
            © {new Date().getFullYear()} All rights reserved
          </Text>
        </Box>
      </Box>
    </Box>
  );
};
